import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from '@/components/Select';
import { apiPutRequest } from '@/utils/apiClient';
import { useUser } from '@/hooks/UserContext';
import { resolveWeekStart, WeekStartSetting } from '@/i18n/weekStart';

const WeekStartPreference = (): ReactElement | null => {
    const { userInfo } = useUser();
    const { t } = useTranslation('settings');

    const [selectedWeekStart, setSelectedWeekStart] = useState<WeekStartSetting>(() =>
        resolveWeekStart(userInfo?.weekStart),
    );

    useEffect(() => {
        setSelectedWeekStart(resolveWeekStart(userInfo?.weekStart));
    }, [userInfo?.weekStart]);

    const weekStartOptions = useMemo(
        () => [
            { value: 'monday', label: t('weekStart.options.monday') },
            { value: 'sunday', label: t('weekStart.options.sunday') },
        ],
        [t],
    );

    const selectedOption = useMemo(() => {
        const normalized = resolveWeekStart(selectedWeekStart);
        const value = normalized === 'sun' ? 'sunday' : 'monday';
        return weekStartOptions.find((option) => option.value === value) ?? weekStartOptions[0];
    }, [weekStartOptions, selectedWeekStart]);

    const saveWeekStart = async (weekStart: string) => {
        try {
            const response = await apiPutRequest('/api/settings/week-start', { weekStart });
            if (!response.ok) throw new Error(`Failed to update week start: ${response.status}`);
            // 変更を即時反映するためにページを再読み込み
            window.location.reload();
        } catch (error) {
            console.error(error);
        }
    };

    const handleWeekStartChange = async (option: { value: string }) => {
        setSelectedWeekStart(option.value as WeekStartSetting);
        // 自動保存
        if (option.value && option.value !== resolveWeekStart(userInfo?.weekStart)) {
            await saveWeekStart(option.value);
        }
    };

    return (
        <section className="flex flex-col gap-3">
            <h3 className="text-lg font-medium">{t('weekStart.heading')}</h3>
            <p className="text-sm text-gray-600">{t('weekStart.description')}</p>
            <div className="max-w-xs">
                <Select
                    options={weekStartOptions}
                    defaultValue={selectedOption}
                    onValueChange={handleWeekStartChange}
                />
            </div>
        </section>
    );
};

export default WeekStartPreference;
